import React, { useState } from 'react'
import { Card, Button, Table, Form, Input, Select, DatePicker, Space, Typography } from 'antd'
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import moment from 'moment'

const { Title, Text } = Typography
const { Option } = Select

const ImmunizationsManagement = ({ immunizations = [], setImmunizations }) => {
  const [editMode, setEditMode] = useState(false)
  const [currentImmunization, setCurrentImmunization] = useState({
    name: '',
    dueDate: null,
    status: 'Scheduled',
    notes: ''
  })
  const [form] = Form.useForm()

  const handleAddImmunization = () => {
    form.validateFields().then((values) => {
      const newImmunization = {
        ...values,
        dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : null,
        id: Date.now().toString() // Generate a unique ID
      }

      if (editMode) {
        const updatedImmunizations = immunizations.map((item) =>
          item.id === currentImmunization.id ? newImmunization : item
        )
        setImmunizations(updatedImmunizations)
        setEditMode(false)
      } else {
        setImmunizations([...immunizations, newImmunization])
      }

      // Reset form
      form.resetFields()
      setCurrentImmunization({
        name: '',
        dueDate: null,
        status: 'Scheduled',
        notes: ''
      })
    })
  }

  const handleEdit = (record) => {
    setEditMode(true)
    setCurrentImmunization(record)
    form.setFieldsValue({
      ...record,
      dueDate: record.dueDate ? moment(record.dueDate) : null
    })
  }

  const handleDelete = (id) => {
    setImmunizations(immunizations.filter((item) => item.id !== id))
  }

  const handleCancel = () => {
    setEditMode(false)
    form.resetFields()
    setCurrentImmunization({
      name: '',
      dueDate: null,
      status: 'Scheduled',
      notes: ''
    })
  }

  const columns = [
    {
      title: 'Immunization Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (text) => (text ? moment(text).format('MM/DD/YYYY') : 'Not scheduled')
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status'
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type='link' icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button type='link' danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
        </Space>
      )
    }
  ]

  return (
    <div className='immunizations-section'>
      <Card title={<Title level={4}>Immunizations Management</Title>}>
        <Text type='secondary' style={{ display: 'block', marginBottom: 16 }}>
          Add immunizations and vaccinations that are part of this preventive care plan
        </Text>

        <Form form={form} layout='vertical' className='form-section'>
          <Form.Item
            name='name'
            label='Immunization Name'
            rules={[{ required: true, message: 'Please enter immunization name' }]}
          >
            <Input placeholder='Enter immunization name' />
          </Form.Item>

          <Form.Item name='dueDate' label='Due Date'>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name='status' label='Status' initialValue='Scheduled'>
            <Select>
              <Option value='Scheduled'>Scheduled</Option>
              <Option value='Completed'>Completed</Option>
              <Option value='Overdue'>Overdue</Option>
              <Option value='Not Required'>Not Required</Option>
            </Select>
          </Form.Item>

          <Form.Item name='notes' label='Notes'>
            <Input.TextArea rows={3} placeholder='Additional notes' />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type='primary' icon={<PlusOutlined />} onClick={handleAddImmunization}>
                {editMode ? 'Update Immunization' : 'Add Immunization'}
              </Button>
              {editMode && <Button onClick={handleCancel}>Cancel</Button>}
            </Space>
          </Form.Item>
        </Form>

        <Table dataSource={immunizations} columns={columns} rowKey='id' pagination={false} className='tracker-table' />
      </Card>
    </div>
  )
}

export default ImmunizationsManagement
